import type { GridFontVariant, GridRendererConfig, GridRgb } from './config'
import type { FontStore } from './font-store'
import {
  ATLAS_PAGE_SIZE,
  GlyphAtlas,
  type AtlasGlyph,
  type AtlasPageStorage,
  type GlyphBitmap,
  type GlyphKey,
  type GlyphRasterRequest,
  type GlyphRasterizer,
} from './atlas'
import { visitGeometry, type GeometryGlyph, type GridGeometry } from './geometry'
import { EMPTY_IMAGE_DRAWS, validateImageDraws, type ImageDraw } from './image-geometry'

export interface CanvasFontResolver {
  isReady(configGeneration: number): boolean
  familyFor(variant: 0 | 1 | 2 | 3, configGeneration: number): string
  availableVariants(configGeneration: number): ReadonlySet<GridFontVariant>
}

export interface CanvasGlyphPainter<Page> extends AtlasPageStorage<Page> {
  paint(context: CanvasRenderingContext2D, glyph: AtlasGlyph<Page>, x: number, y: number,
        color: GridRgb, alpha: number): void
  paintDirect(context: CanvasRenderingContext2D, bitmap: GlyphBitmap,
              request: GlyphRasterRequest, x: number, y: number,
              color: GridRgb, alpha: number): void
  dispose?(): void
}

export interface CanvasRenderFrame {
  readonly geometry: GridGeometry
  readonly images?: readonly ImageDraw[]
  readonly config: GridRendererConfig
  readonly cols: number
  readonly rows: number
  readonly dpr: number
  readonly viewportWidth?: number
  readonly viewportHeight?: number
}

export interface CanvasRendererOptions<Page> {
  readonly storage?: CanvasGlyphPainter<Page>
  readonly rasterizer?: GlyphRasterizer
  readonly canvasFactory?: () => HTMLCanvasElement
}

interface ConfigurableRasterizer extends GlyphRasterizer {
  configure(config: GridRendererConfig, dpr: number): void
  dispose(): void
}

interface CanvasAtlasPage {
  readonly canvas: HTMLCanvasElement
  readonly context: CanvasRenderingContext2D
}

export const CANVAS_DIRECT_GLYPH_MAX_PIXELS = ATLAS_PAGE_SIZE * ATLAS_PAGE_SIZE
export const CANVAS_DIRECT_GLYPH_MAX_DIMENSION = 0x7fff
export const CANVAS_BACKING_MAX_DIMENSION = 8192
export const CANVAS_BACKING_MAX_PIXELS = 16 * 1024 * 1024

interface CanvasFrameSize {
  readonly width: number
  readonly height: number
  readonly backingWidth: number
  readonly backingHeight: number
}

function validateCanvasBacking(width: number, height: number, label: string): void {
  const pixels = width * height
  if (!Number.isSafeInteger(width) || width < 0 ||
      !Number.isSafeInteger(height) || height < 0 ||
      width > CANVAS_BACKING_MAX_DIMENSION || height > CANVAS_BACKING_MAX_DIMENSION ||
      !Number.isSafeInteger(pixels) || pixels > CANVAS_BACKING_MAX_PIXELS) {
    throw new RangeError(`Canvas ${label} exceeds supported bounds`)
  }
}

function canvasFrameSize(frame: CanvasRenderFrame): CanvasFrameSize {
  const { config } = frame
  if (!Number.isInteger(frame.cols) || frame.cols < 0 || frame.cols > 500 ||
      !Number.isInteger(frame.rows) || frame.rows < 0 || frame.rows > 200 ||
      !Number.isFinite(frame.dpr) || frame.dpr <= 0 || frame.dpr > 16) {
    throw new RangeError('Canvas renderer dimensions or DPR are invalid')
  }
  const gridWidth = frame.cols * config.cellWidth
  const gridHeight = frame.rows * config.cellHeight
  if (!Number.isSafeInteger(gridWidth) || !Number.isSafeInteger(gridHeight)) {
    throw new RangeError('Canvas renderer logical size is invalid')
  }
  const ownsViewport = frame.viewportWidth !== undefined || frame.viewportHeight !== undefined
  if (ownsViewport && (frame.viewportWidth === undefined || frame.viewportHeight === undefined ||
      !Number.isFinite(frame.viewportWidth) || frame.viewportWidth < 0 ||
      !Number.isFinite(frame.viewportHeight) || frame.viewportHeight < 0)) {
    throw new RangeError('Canvas renderer viewport is invalid')
  }
  const width = frame.viewportWidth ?? gridWidth
  const height = frame.viewportHeight ?? gridHeight
  const cssWidth = Math.ceil(width)
  const cssHeight = Math.ceil(height)
  const cssPixels = cssWidth * cssHeight
  if (ownsViewport && (!Number.isSafeInteger(cssWidth) || !Number.isSafeInteger(cssHeight) ||
      cssWidth > CANVAS_BACKING_MAX_DIMENSION || cssHeight > CANVAS_BACKING_MAX_DIMENSION ||
      !Number.isSafeInteger(cssPixels) || cssPixels > CANVAS_BACKING_MAX_PIXELS)) {
    throw new RangeError('Canvas viewport backing exceeds supported bounds')
  }
  const scaledWidth = width * frame.dpr
  const scaledHeight = height * frame.dpr
  const backingWidth = Math.round(scaledWidth)
  const backingHeight = Math.round(scaledHeight)
  const backingPixels = backingWidth * backingHeight
  if (!Number.isFinite(scaledWidth) || !Number.isFinite(scaledHeight) ||
      !Number.isSafeInteger(backingWidth) || !Number.isSafeInteger(backingHeight) ||
      (width > 0 && backingWidth === 0) || (height > 0 && backingHeight === 0) ||
      backingWidth > CANVAS_BACKING_MAX_DIMENSION || backingHeight > CANVAS_BACKING_MAX_DIMENSION ||
      !Number.isSafeInteger(backingPixels) || backingPixels > CANVAS_BACKING_MAX_PIXELS) {
    throw new RangeError('Canvas backing size exceeds supported bounds')
  }
  return Object.freeze({ width, height, backingWidth, backingHeight })
}

function cssColor(color: GridRgb): string {
  return `rgb(${color[0]} ${color[1]} ${color[2]})`
}

function setCanvasSizeExact(canvas: HTMLCanvasElement, width: number,
                            height: number, label: string): void {
  // Each dimension assignment may immediately reallocate using the other
  // dimension's current value. Collapse the old backing first so an
  // aspect-ratio flip never exceeds the already-validated endpoint budget.
  if (canvas.width !== 0) canvas.width = 0
  if (canvas.height !== 0) canvas.height = 0
  canvas.width = width
  canvas.height = height
  if (canvas.width !== width || canvas.height !== height) {
    throw new RangeError(`Canvas ${label} backing surface rejected validated dimensions`)
  }
}

function releaseCanvasBacking(canvas: HTMLCanvasElement): void {
  // Cleanup is best-effort and each assignment is independent: a platform
  // failure releasing one dimension must not prevent collapsing the other.
  try { canvas.width = 0 } catch {}
  try { canvas.height = 0 } catch {}
}

function bestEffortCleanup(action: () => void): void {
  try { action() } catch {}
}

function glyphKey(command: GeometryGlyph): GlyphKey {
  return {
    codepoint: command.codepoint,
    variant: command.variant,
    embolden: command.embolden,
    shear: command.shear,
  }
}

class CanvasPageStorage implements CanvasGlyphPainter<CanvasAtlasPage> {
  readonly #tint = document.createElement('canvas')
  readonly #direct = document.createElement('canvas')
  readonly #directTint = document.createElement('canvas')

  createPage(_index: number, size: number): CanvasAtlasPage {
    const canvas = document.createElement('canvas')
    setCanvasSizeExact(canvas, size, size, 'atlas page')
    const context = canvas.getContext('2d')
    if (context === null) throw new Error('Canvas2D atlas page is unavailable')
    context.imageSmoothingEnabled = false
    return { canvas, context }
  }

  clearPage(page: CanvasAtlasPage): void {
    page.context.clearRect(0, 0, page.canvas.width, page.canvas.height)
  }

  destroyPage(page: CanvasAtlasPage): void {
    page.canvas.width = 0
    page.canvas.height = 0
  }

  upload(page: CanvasAtlasPage, x: number, y: number, bitmap: GlyphBitmap): void {
    this.#uploadAlpha(page.context, x, y, bitmap)
  }

  paint(context: CanvasRenderingContext2D, glyph: AtlasGlyph<CanvasAtlasPage>, x: number, y: number,
        color: GridRgb, alpha: number): void {
    if (glyph.width === 0 || glyph.height === 0) return
    if (this.#tint.width < glyph.width) this.#tint.width = glyph.width
    if (this.#tint.height < glyph.height) this.#tint.height = glyph.height
    if (this.#tint.width < glyph.width || this.#tint.height < glyph.height) {
      throw new RangeError('Canvas2D glyph tint surface cannot represent atlas glyph')
    }
    this.#paintSource(context, this.#tint, glyph.page.canvas,
      glyph.x, glyph.y, glyph.width, glyph.height,
      x + glyph.offsetX, y + glyph.offsetY, glyph.dpr, color, alpha)
  }

  paintDirect(context: CanvasRenderingContext2D, bitmap: GlyphBitmap,
              request: GlyphRasterRequest, x: number, y: number,
              color: GridRgb, alpha: number): void {
    if (bitmap.width === 0 || bitmap.height === 0) return
    setCanvasSizeExact(this.#direct, bitmap.width, bitmap.height, 'direct glyph')
    setCanvasSizeExact(this.#directTint, bitmap.width, bitmap.height, 'direct glyph tint')
    const direct = this.#direct.getContext('2d')
    if (direct === null) throw new Error('Canvas2D direct glyph surface is unavailable')
    this.#uploadAlpha(direct, 0, 0, bitmap)
    this.#paintSource(context, this.#directTint, this.#direct,
      0, 0, bitmap.width, bitmap.height,
      x + bitmap.offsetX, y + bitmap.offsetY, request.dpr, color, alpha)
  }

  dispose(): void {
    releaseCanvasBacking(this.#tint)
    releaseCanvasBacking(this.#direct)
    releaseCanvasBacking(this.#directTint)
  }

  #paintSource(context: CanvasRenderingContext2D, tintCanvas: HTMLCanvasElement,
               source: CanvasImageSource, sourceX: number, sourceY: number,
               width: number, height: number, destinationX: number, destinationY: number,
               dpr: number, color: GridRgb, alpha: number): void {
    const tint = tintCanvas.getContext('2d')
    if (tint === null) throw new Error('Canvas2D glyph tint surface is unavailable')
    tint.imageSmoothingEnabled = false
    tint.clearRect(0, 0, width, height)
    tint.globalCompositeOperation = 'source-over'
    tint.drawImage(source, sourceX, sourceY, width, height, 0, 0, width, height)
    tint.globalCompositeOperation = 'source-in'
    tint.fillStyle = cssColor(color)
    tint.fillRect(0, 0, width, height)
    tint.globalCompositeOperation = 'source-over'
    context.globalAlpha = alpha / 255
    context.drawImage(tintCanvas, 0, 0, width, height,
      destinationX, destinationY, width / dpr, height / dpr)
  }

  #uploadAlpha(context: CanvasRenderingContext2D, x: number, y: number,
               bitmap: GlyphBitmap): void {
    const rgba = new Uint8ClampedArray(bitmap.width * bitmap.height * 4)
    for (let source = 0, target = 0; source < bitmap.alpha.length; source++, target += 4) {
      rgba[target] = 255
      rgba[target + 1] = 255
      rgba[target + 2] = 255
      rgba[target + 3] = bitmap.alpha[source]
    }
    context.putImageData(new ImageData(rgba, bitmap.width, bitmap.height), x, y)
  }
}

class CanvasGlyphRasterizer implements ConfigurableRasterizer {
  readonly #canvas = document.createElement('canvas')
  readonly #fonts: CanvasFontResolver
  #config: GridRendererConfig | null = null
  #dpr = 1
  #disposed = false

  constructor(fonts: CanvasFontResolver) {
    this.#fonts = fonts
  }

  configure(config: GridRendererConfig, dpr: number): void {
    if (this.#disposed) throw new Error('Canvas glyph rasterizer is disposed')
    this.#config = config
    this.#dpr = dpr
  }

  async rasterize(request: GlyphRasterRequest): Promise<GlyphBitmap> {
    if (this.#disposed) throw new Error('Canvas glyph rasterizer is disposed')
    const config = this.#config
    if (config === null || request.configGeneration !== config.configGeneration || request.dpr !== this.#dpr ||
        !this.#fonts.isReady(config.configGeneration)) throw new Error('font rasterization configuration is stale')
    const context = this.#canvas.getContext('2d', { willReadFrequently: true })
    if (context === null) throw new Error('Canvas2D glyph rasterizer is unavailable')
    const family = this.#fonts.familyFor(request.variant, config.configGeneration)
    const italic = request.variant === 2 || request.variant === 3
    const bold = request.variant === 1 || request.variant === 3
    // cssPxSize, not fontSize: CSS px means the em, while the server sized these
    // cells by (ascent - descent). See GridRendererConfig.cssPxSize.
    const font = `${italic ? 'italic ' : ''}${bold ? '700 ' : '400 '}${config.cssPxSize * request.dpr}px "${family}"`
    context.font = font
    context.textAlign = 'left'
    context.textBaseline = 'alphabetic'
    const text = String.fromCodePoint(request.codepoint)
    const metrics = context.measureText(text)
    const left = Math.max(0, Math.ceil(metrics.actualBoundingBoxLeft || 0))
    const right = Math.max(0, Math.ceil(metrics.actualBoundingBoxRight || metrics.width))
    const ascent = Math.max(0, Math.ceil(metrics.actualBoundingBoxAscent || config.baseline * request.dpr))
    const descent = Math.max(0, Math.ceil(metrics.actualBoundingBoxDescent ||
      Math.max(0, config.cellHeight - config.baseline) * request.dpr))
    const shearPad = request.shear ? Math.ceil((ascent + descent) * 0.2) : 0
    const emboldenPad = request.embolden ? 1 : 0
    const width = left + right + shearPad + emboldenPad
    const height = ascent + descent
    const pixels = width * height
    if (!Number.isSafeInteger(width) || width < 0 || !Number.isSafeInteger(height) || height < 0 ||
        width > CANVAS_DIRECT_GLYPH_MAX_DIMENSION || height > CANVAS_DIRECT_GLYPH_MAX_DIMENSION ||
        !Number.isSafeInteger(pixels) || pixels > CANVAS_DIRECT_GLYPH_MAX_PIXELS) {
      throw new RangeError('Canvas direct glyph exceeds bounded raster surface')
    }
    if (width === 0 || height === 0) {
      return { width: 0, height: 0, offsetX: 0, offsetY: 0, alpha: new Uint8Array() }
    }

    setCanvasSizeExact(this.#canvas, width, height, 'direct glyph rasterizer')
    const draw = this.#canvas.getContext('2d', { willReadFrequently: true })
    if (draw === null) throw new Error('Canvas2D glyph rasterizer is unavailable')
    draw.clearRect(0, 0, width, height)
    draw.font = font
    draw.textAlign = 'left'
    draw.textBaseline = 'alphabetic'
    draw.fillStyle = 'white'
    if (request.shear) draw.setTransform(1, 0, -0.2, 1, shearPad, 0)
    draw.fillText(text, left, ascent)
    if (request.embolden) draw.fillText(text, left + 1, ascent)
    draw.setTransform(1, 0, 0, 1, 0, 0)
    const rgba = draw.getImageData(0, 0, width, height).data
    const alpha = new Uint8Array(width * height)
    for (let source = 3, target = 0; source < rgba.length; source += 4, target++) alpha[target] = rgba[source]
    return {
      width, height,
      // Match native quad shear: the baseline/lower edge stays at the
      // ordinary glyph x while only the top leans right into shearPad.
      offsetX: -left / request.dpr,
      offsetY: -ascent / request.dpr,
      alpha,
    }
  }

  dispose(): void {
    if (this.#disposed) return
    this.#disposed = true
    this.#config = null
    releaseCanvasBacking(this.#canvas)
  }
}

interface PendingCanvasRender<Page> {
  readonly epoch: number
  readonly canvas: HTMLCanvasElement
  readonly context: CanvasRenderingContext2D
  readonly atlas: GlyphAtlas<Page>
  ownsCanvas: boolean
  ownsAtlas: boolean
}

export class CanvasRenderer<Page = CanvasAtlasPage> {
  readonly #canvas: HTMLCanvasElement
  readonly #context: CanvasRenderingContext2D
  readonly #fonts: CanvasFontResolver
  readonly #storage: CanvasGlyphPainter<Page>
  readonly #rasterizer: GlyphRasterizer
  readonly #configurableRasterizer: ConfigurableRasterizer | null
  readonly #canvasFactory: () => HTMLCanvasElement
  #atlas: GlyphAtlas<Page>
  #pending: PendingCanvasRender<Page> | null = null
  #renderEpoch = 0
  #logicalWidth = 0
  #logicalHeight = 0
  #dpr = 0
  #disposed = false

  constructor(canvas: HTMLCanvasElement, fonts: FontStore | CanvasFontResolver,
              options: CanvasRendererOptions<Page> = {}) {
    const context = canvas.getContext('2d')
    if (context === null) throw new Error('Canvas2D renderer is unavailable')
    this.#canvas = canvas
    this.#context = context
    this.#fonts = fonts
    this.#canvasFactory = options.canvasFactory ?? (() => canvas.cloneNode(false) as HTMLCanvasElement)
    const defaultStorage = options.storage === undefined ? new CanvasPageStorage() : null
    this.#storage = options.storage ?? defaultStorage as unknown as CanvasGlyphPainter<Page>
    const defaultRasterizer = options.rasterizer === undefined ? new CanvasGlyphRasterizer(fonts) : null
    this.#rasterizer = options.rasterizer ?? defaultRasterizer!
    this.#configurableRasterizer = defaultRasterizer
    this.#atlas = new GlyphAtlas(0, 1, this.#rasterizer, this.#storage)
  }

  get logicalSize(): Readonly<{ width: number; height: number; dpr: number }> {
    return Object.freeze({ width: this.#logicalWidth, height: this.#logicalHeight, dpr: this.#dpr })
  }

  async render(frame: CanvasRenderFrame): Promise<boolean> {
    if (this.#disposed) throw new Error('Canvas renderer is disposed')
    const { config, geometry } = frame
    const images = frame.images ?? EMPTY_IMAGE_DRAWS
    validateImageDraws(images, { requireSource: true })
    if (!this.#fonts.isReady(config.configGeneration)) return false
    const size = canvasFrameSize(frame)
    validateCanvasBacking(this.#canvas.width, this.#canvas.height, 'existing visible backing')
    const epoch = ++this.#renderEpoch
    this.#cancelPending()

    const staging = this.#canvasFactory()
    if (staging === this.#canvas) throw new Error('Canvas staging surface must be private')
    try {
      setCanvasSizeExact(staging, size.backingWidth, size.backingHeight, 'staging')
    } catch (error) {
      releaseCanvasBacking(staging)
      throw error
    }
    const stagingContext = staging.getContext('2d')
    if (stagingContext === null) {
      releaseCanvasBacking(staging)
      throw new Error('Canvas2D staging renderer is unavailable')
    }

    const associationChanged = this.#atlas.configGeneration !== config.configGeneration ||
      this.#atlas.dpr !== frame.dpr
    const atlas = associationChanged
      ? new GlyphAtlas(config.configGeneration, frame.dpr, this.#rasterizer, this.#storage)
      : this.#atlas
    const pending: PendingCanvasRender<Page> = {
      epoch, canvas: staging, context: stagingContext, atlas,
      ownsCanvas: true, ownsAtlas: associationChanged,
    }
    this.#pending = pending
    this.#configurableRasterizer?.configure(config, frame.dpr)

    try {
      stagingContext.setTransform(frame.dpr, 0, 0, frame.dpr, 0, 0)
      stagingContext.imageSmoothingEnabled = false
      this.#fillRect(stagingContext, 0, 0, size.width, size.height, config.background, 255)

      // Canonical native order without a retained flat command list: first
      // backgrounds and procedural boxes, then each glyph immediately after
      // its single raster/cache lookup, then decorations and overlays.
      visitGeometry(geometry, {
        rect: (layer, _primitive, x, y, width, height, color, alpha) => {
          if (layer === 'background' || layer === 'content') {
            this.#fillRect(stagingContext, x, y, width, height, color, alpha)
          }
        },
        glyph: () => {},
      })
      for (const command of geometry.content) {
        if (command.kind !== 'glyph') continue
        if (!await this.#paintGlyph(pending, command, config, frame.dpr)) return false
      }
      stagingContext.globalAlpha = 1
      for (const draw of images) {
        stagingContext.imageSmoothingEnabled = draw.filter === 'linear'
        stagingContext.drawImage(draw.source!,
          draw.sourceX, draw.sourceY, draw.sourceWidth, draw.sourceHeight,
          draw.destinationX, draw.destinationY,
          draw.destinationWidth, draw.destinationHeight)
      }
      visitGeometry(geometry, {
        rect: (layer, _primitive, x, y, width, height, color, alpha) => {
          if (layer === 'decoration' || layer === 'selection' || layer === 'cursor') {
            this.#fillRect(stagingContext, x, y, width, height, color, alpha)
          }
        },
        glyph: () => {},
      })
      stagingContext.globalAlpha = 1
      if (!this.#renderIsCurrent(pending, config.configGeneration, frame.dpr)) return false

      this.#commitVisible(staging, size)
      let previous: GlyphAtlas<Page> | null = null
      if (pending.ownsAtlas) {
        previous = this.#atlas
        this.#atlas = atlas
        pending.ownsAtlas = false
      }
      this.#logicalWidth = size.width
      this.#logicalHeight = size.height
      this.#dpr = frame.dpr
      const obsolete = previous
      if (obsolete !== null) bestEffortCleanup(() => { obsolete.dispose() })
      return true
    } finally {
      if (this.#pending === pending) this.#pending = null
      if (pending.ownsCanvas) {
        pending.ownsCanvas = false
        releaseCanvasBacking(staging)
      }
      if (pending.ownsAtlas) bestEffortCleanup(() => { atlas.dispose() })
    }
  }

  dispose(): void {
    if (this.#disposed) return
    this.#disposed = true
    ++this.#renderEpoch
    this.#cancelPending()
    bestEffortCleanup(() => { this.#atlas.dispose() })
    bestEffortCleanup(() => { this.#storage.dispose?.() })
    bestEffortCleanup(() => { this.#configurableRasterizer?.dispose() })
    this.#logicalWidth = 0
    this.#logicalHeight = 0
    this.#dpr = 0
  }

  #fillRect(context: CanvasRenderingContext2D, x: number, y: number,
            width: number, height: number, color: GridRgb, alpha: number): void {
    if (width <= 0 || height <= 0) return
    context.fillStyle = cssColor(color)
    context.globalAlpha = alpha / 255
    context.fillRect(x, y, width, height)
  }

  async #paintGlyph(pending: PendingCanvasRender<Page>, command: GeometryGlyph,
                    config: GridRendererConfig, dpr: number): Promise<boolean> {
    const result = await pending.atlas.get(glyphKey(command))
    if (!this.#renderIsCurrent(pending, config.configGeneration, dpr)) return false
    if (result === null) {
      const request: GlyphRasterRequest = Object.freeze({
        ...glyphKey(command), configGeneration: config.configGeneration, dpr,
      })
      const bitmap = await this.#rasterizer.rasterize(request)
      if (!this.#renderIsCurrent(pending, config.configGeneration, dpr)) return false
      this.#paintDirect(pending.context, command, config, request, bitmap)
      return true
    }
    if (result.kind === 'uncached') {
      this.#paintDirect(pending.context, command, config, result.request, result.bitmap)
      return true
    }
    if (!pending.atlas.isCurrent(result)) return false
    this.#storage.paint(pending.context, result, command.x,
      command.y + config.baseline, command.color, command.alpha)
    return true
  }

  #paintDirect(context: CanvasRenderingContext2D, command: GeometryGlyph,
               config: GridRendererConfig, request: GlyphRasterRequest,
               bitmap: GlyphBitmap): void {
    const pixels = bitmap.width * bitmap.height
    if (!Number.isInteger(bitmap.width) || bitmap.width < 0 ||
        !Number.isInteger(bitmap.height) || bitmap.height < 0 ||
        bitmap.width > CANVAS_DIRECT_GLYPH_MAX_DIMENSION ||
        bitmap.height > CANVAS_DIRECT_GLYPH_MAX_DIMENSION ||
        !Number.isSafeInteger(pixels) || pixels > CANVAS_DIRECT_GLYPH_MAX_PIXELS ||
        !Number.isFinite(bitmap.offsetX) || !Number.isFinite(bitmap.offsetY) ||
        !(bitmap.alpha instanceof Uint8Array) || bitmap.alpha.byteLength !== pixels) {
      throw new RangeError('Canvas direct glyph exceeds bounded raster surface')
    }
    this.#storage.paintDirect(context, bitmap, request, command.x,
      command.y + config.baseline, command.color, command.alpha)
  }

  #renderIsCurrent(pending: PendingCanvasRender<Page>, configGeneration: number,
                   dpr: number): boolean {
    return !this.#disposed && pending === this.#pending && pending.epoch === this.#renderEpoch &&
      this.#fonts.isReady(configGeneration) && pending.atlas.configGeneration === configGeneration &&
      pending.atlas.dpr === dpr
  }

  #cancelPending(): void {
    const pending = this.#pending
    if (pending === null) return
    this.#pending = null
    // A suspended render checks currentness before its next paint. Transfer
    // ownership before release so its eventual finally cannot clear a surface
    // which a caller-supplied pool has already reused.
    if (pending.ownsCanvas) {
      pending.ownsCanvas = false
      releaseCanvasBacking(pending.canvas)
    }
    if (pending.ownsAtlas) {
      pending.ownsAtlas = false
      bestEffortCleanup(() => { pending.atlas.dispose() })
    }
  }

  #commitVisible(staging: HTMLCanvasElement, size: CanvasFrameSize): void {
    const oldWidth = this.#canvas.width
    const oldHeight = this.#canvas.height
    validateCanvasBacking(oldWidth, oldHeight, 'existing visible backing')
    const oldStyleWidth = this.#canvas.style.width
    const oldStyleHeight = this.#canvas.style.height
    const backingChanged = oldWidth !== size.backingWidth || oldHeight !== size.backingHeight
    const backup = this.#canvasFactory()
    if (backup === this.#canvas || backup === staging) {
      throw new Error('Canvas backup surface must be private')
    }
    try {
      setCanvasSizeExact(backup, oldWidth, oldHeight, 'backup')
      const backupContext = backup.getContext('2d')
      if (backupContext === null) throw new Error('Canvas2D backup renderer is unavailable')
      if (oldWidth > 0 && oldHeight > 0) {
        backupContext.setTransform(1, 0, 0, 1, 0, 0)
        backupContext.globalAlpha = 1
        backupContext.drawImage(this.#canvas, 0, 0)
      }

      let visibleChanged = false
      try {
        visibleChanged = true
        if (backingChanged) {
          setCanvasSizeExact(this.#canvas, size.backingWidth, size.backingHeight, 'visible')
        }
        this.#context.setTransform(1, 0, 0, 1, 0, 0)
        this.#context.globalAlpha = 1
        this.#context.imageSmoothingEnabled = false
        if (size.backingWidth > 0 && size.backingHeight > 0) this.#context.drawImage(staging, 0, 0)
        if (this.#canvas.style.width !== `${size.width}px`) this.#canvas.style.width = `${size.width}px`
        if (this.#canvas.style.height !== `${size.height}px`) this.#canvas.style.height = `${size.height}px`
      } catch (error) {
        if (visibleChanged) {
          if (backingChanged) {
            bestEffortCleanup(() => { setCanvasSizeExact(this.#canvas, oldWidth, oldHeight, 'visible rollback') })
          }
          bestEffortCleanup(() => { this.#canvas.style.width = oldStyleWidth })
          bestEffortCleanup(() => { this.#canvas.style.height = oldStyleHeight })
          if (this.#canvas.width === oldWidth && this.#canvas.height === oldHeight && oldWidth > 0 && oldHeight > 0) {
            bestEffortCleanup(() => {
              this.#context.setTransform(1, 0, 0, 1, 0, 0)
              this.#context.globalAlpha = 1
              this.#context.imageSmoothingEnabled = false
              this.#context.drawImage(backup, 0, 0)
            })
          }
        }
        throw error
      }
    } finally {
      releaseCanvasBacking(backup)
    }
  }
}

export { CanvasGlyphRasterizer, CanvasPageStorage }
